import React, { useMemo, useRef, useState, useCallback } from 'react';
import {
    Box,
    Button,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import * as XLSX from 'xlsx';
import { useTranslation } from 'react-i18next';

const REQUIRED_SUFFIX = ' * (חובה)';

/** Hebrew headers for the downloadable template (first row of the sheet). */
const HE_COLUMNS = {
    makat: `מק"ט / קוד פריט${REQUIRED_SUFFIX}`,
    name: `שם פריט${REQUIRED_SUFFIX}`,
    price: `מחיר${REQUIRED_SUFFIX}`,
    includeVat: 'מחיר כולל מע"מ (כן / לא)',
};

const BOOL_TRUE = new Set(['כן', 'yes', 'true', '1', 'y', 't']);
const BOOL_FALSE = new Set(['לא', 'no', 'false', '0', 'n', 'f']);

const parseBoolCell = (v, defaultVal = true) => {
    if (v === undefined || v === null || v === '') return defaultVal;
    const s = String(v).trim().toLowerCase();
    if (BOOL_TRUE.has(s)) return true;
    if (BOOL_FALSE.has(s)) return false;
    return defaultVal;
};

const normalizeHeader = (s) =>
    String(s || '')
        .replace(/\(.*?\)/g, ' ')
        .replace(/[*״"']/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

const buildTemplateWorkbook = (sampleRows = []) => {
    const headerOrder = [HE_COLUMNS.makat, HE_COLUMNS.name, HE_COLUMNS.price, HE_COLUMNS.includeVat];
    const rows = [headerOrder, ...sampleRows];
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = headerOrder.map((h) => ({ wch: Math.min(40, Math.max(12, h.length + 2)) }));
    ws['!views'] = [{ RTL: true }];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'פריטים');

    const notes = [
        ['שדה', 'הערות'],
        [HE_COLUMNS.makat, 'מזהה ייחודי לפריט (טקסט או מספר).'],
        [HE_COLUMNS.name, 'שם התצוגה של הפריט.'],
        [HE_COLUMNS.price, 'מחיר ליחידה — ניתן להזין עם נקודה או פסיק עשרוני.'],
        [HE_COLUMNS.includeVat, 'כן או לא. אם ריק — נחשב כן.'],
    ];
    const wsNotes = XLSX.utils.aoa_to_sheet(notes);
    wsNotes['!cols'] = [{ wch: 36 }, { wch: 72 }];
    wsNotes['!views'] = [{ RTL: true }];
    XLSX.utils.book_append_sheet(wb, wsNotes, 'הוראות');

    return wb;
};

function findColumnIndex(headerRow, canonicalLabel) {
    const normTarget = normalizeHeader(canonicalLabel);
    return headerRow.findIndex((h) => {
        if (!h) return false;
        const hs = String(h).trim();
        if (hs === canonicalLabel) return true;
        return normalizeHeader(hs) === normTarget;
    });
}

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {string} props.direction
 * @param {Array<{ makat: string, name?: string }>} props.existingItems
 * @param {(items: Array<{ makat: string, name: string, price: number, includeVat: boolean }>) => Promise<boolean>} props.onCommit
 */
function ClientItemsExcelImportModal({ open, onClose, direction, existingItems = [], onCommit }) {
    const { t } = useTranslation();
    const fileInputRef = useRef(null);
    const [fileName, setFileName] = useState('');
    const [parseError, setParseError] = useState('');
    const [parsedRows, setParsedRows] = useState([]);
    const [committing, setCommitting] = useState(false);

    const existingMakatSet = useMemo(() => {
        const s = new Set();
        (existingItems || []).forEach((it) => {
            if (it?.makat != null && String(it.makat).trim() !== '') s.add(String(it.makat).trim());
        });
        return s;
    }, [existingItems]);

    const handleDownloadTemplate = useCallback(() => {
        const sample = [['1001', 'דוגמה לפריט', 99.9, 'כן']];
        const wb = buildTemplateWorkbook(sample);
        XLSX.writeFile(wb, 'Xtram_ClientItems_Template.xlsx');
    }, []);

    const resetState = useCallback(() => {
        setFileName('');
        setParseError('');
        setParsedRows([]);
    }, []);

    const handleClose = () => {
        resetState();
        onClose();
    };

    const handleFileSelected = async (e) => {
        setParseError('');
        const file = e.target.files?.[0];
        if (!file) return;
        setFileName(file.name);
        try {
            const buf = await file.arrayBuffer();
            const wb = XLSX.read(buf, { type: 'array', cellDates: true });
            const ws = wb.Sheets[wb.SheetNames[0]];
            if (!ws) {
                setParseError(t('ClientItemsExcelReadFailed') || 'Could not read spreadsheet');
                setParsedRows([]);
                return;
            }

            const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '', raw: false, blankrows: false });
            if (!aoa || aoa.length === 0) {
                setParsedRows([]);
                setParseError(t('ClientItemsExcelEmptyFile') || 'File is empty');
                return;
            }

            const headerRow = aoa[0].map((h) => String(h || '').trim());
            const col = {
                makat: findColumnIndex(headerRow, HE_COLUMNS.makat),
                name: findColumnIndex(headerRow, HE_COLUMNS.name),
                price: findColumnIndex(headerRow, HE_COLUMNS.price),
                includeVat: findColumnIndex(headerRow, HE_COLUMNS.includeVat),
            };

            if (col.makat === -1 || col.name === -1 || col.price === -1) {
                setParseError(
                    t('ClientItemsExcelMissingColumns') ||
                        'Required columns not found. Download the template and use the Hebrew headers in the first row.'
                );
                setParsedRows([]);
                return;
            }

            const dataRows = aoa.slice(1);
            const rows = [];
            dataRows.forEach((raw, i) => {
                const hasAny = raw.some((c) => String(c ?? '').trim() !== '');
                if (!hasAny) return;

                const makat = col.makat >= 0 ? String(raw[col.makat] ?? '').trim() : '';
                const name = col.name >= 0 ? String(raw[col.name] ?? '').trim() : '';
                const priceRaw = col.price >= 0 ? raw[col.price] : '';
                const priceStr =
                    priceRaw === '' || priceRaw === null || priceRaw === undefined
                        ? ''
                        : String(priceRaw).replace(/,/g, '').trim();
                const price = priceStr === '' ? NaN : Number(priceStr);
                const includeVat =
                    col.includeVat >= 0 ? parseBoolCell(raw[col.includeVat], true) : true;

                const errors = [];
                if (!makat) errors.push(t('ClientItemsExcelErrMakatRequired') || 'מק"ט חובה');
                if (!name) errors.push(t('ClientItemsExcelErrNameRequired') || 'שם חובה');
                if (priceRaw === '' || priceRaw === null || priceRaw === undefined || Number.isNaN(price) || price < 0) {
                    errors.push(t('ClientItemsExcelErrPriceInvalid') || 'מחיר לא תקין');
                }

                rows.push({
                    __row: i + 2,
                    makat,
                    name,
                    price: Number.isFinite(price) ? price : 0,
                    includeVat,
                    errors,
                });
            });

            const makatSeen = new Map();
            rows.forEach((r) => {
                if (!r.makat) return;
                makatSeen.set(r.makat, (makatSeen.get(r.makat) || 0) + 1);
            });
            rows.forEach((r) => {
                if (r.makat && makatSeen.get(r.makat) > 1) {
                    r.errors.push(t('ClientItemsExcelErrDuplicateMakatInFile') || 'מק"ט כפול בקובץ');
                }
                if (r.makat && existingMakatSet.has(r.makat)) {
                    r.errors.push(t('ClientItemsExcelErrMakatExists') || 'מק"ט כבר קיים במערכת');
                }
            });

            setParsedRows(rows);
            if (rows.length === 0) {
                setParseError(t('ClientItemsExcelNoDataRows') || 'No data rows after the header');
            }
        } catch (err) {
            console.error(err);
            setParseError(t('ClientItemsExcelParseError') || 'Failed to parse file');
            setParsedRows([]);
        }
        e.target.value = '';
    };

    const validRows = useMemo(() => parsedRows.filter((r) => r.errors.length === 0), [parsedRows]);

    const handleCommit = async () => {
        if (validRows.length === 0) return;
        setCommitting(true);
        try {
            const items = validRows.map((r) => ({
                makat: r.makat,
                name: r.name,
                price: r.price,
                includeVat: r.includeVat,
            }));
            const ok = await onCommit(items);
            if (ok) handleClose();
        } catch (err) {
            console.error(err);
        } finally {
            setCommitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth dir={direction}>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>{t('ClientItemsExcelImportTitle') || 'ייבוא פריטים מאקסל'}</span>
                <IconButton aria-label="close" onClick={handleClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers dir={direction}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        {t('ClientItemsExcelImportHint') ||
                            'הורידו את התבנית בעברית, מלאו את השורות, העלו את הקובץ, לחצו תצוגה מקדימה ואז ייבוא.'}
                    </Typography>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
                        <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownloadTemplate}>
                            {t('DownloadClientItemsTemplate') || 'הורדת תבנית (עמודות בעברית)'}
                        </Button>
                        <Button variant="outlined" component="label" startIcon={<UploadFileIcon />}>
                            {t('ClientItemsExcelChooseFile') || 'בחירת קובץ'}
                            <input
                                ref={fileInputRef}
                                type="file"
                                hidden
                                accept=".xlsx,.xls"
                                onChange={handleFileSelected}
                            />
                        </Button>
                        {fileName ? (
                            <Typography variant="body2" component="span">
                                {fileName}
                            </Typography>
                        ) : null}
                    </Box>

                    {parseError ? (
                        <Typography color="error" variant="body2">
                            {parseError}
                        </Typography>
                    ) : null}

                    {parsedRows.length > 0 ? (
                        <>
                            <Typography variant="body2">
                                {t('ClientItemsExcelRowSummary', { total: parsedRows.length, valid: validRows.length })}
                            </Typography>

                            <TableContainer component={Paper} sx={{ maxHeight: 420 }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>#</TableCell>
                                            <TableCell>{t('ClientItemsExcelColMakat')}</TableCell>
                                            <TableCell>{t('ItemName')}</TableCell>
                                            <TableCell align="right">{t('price')}</TableCell>
                                            <TableCell>{t('ClientItemsExcelColVat')}</TableCell>
                                            <TableCell>{t('ClientItemsExcelColStatus')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {parsedRows.map((r) => (
                                            <TableRow key={r.__row}>
                                                <TableCell>{r.__row}</TableCell>
                                                <TableCell>{r.makat}</TableCell>
                                                <TableCell>{r.name}</TableCell>
                                                <TableCell align="right">{r.price}</TableCell>
                                                <TableCell>{r.includeVat ? 'כן' : 'לא'}</TableCell>
                                                <TableCell>
                                                    {r.errors.length === 0 ? (
                                                        <Chip size="small" color="success" label={t('Valid')} />
                                                    ) : (
                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                            {r.errors.map((err, i) => (
                                                                <Chip key={i} size="small" color="error" label={err} />
                                                            ))}
                                                        </Box>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    ) : null}
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={handleClose}>{t('Cancel') || 'ביטול'}</Button>
                <Button
                    variant="contained"
                    color="success"
                    disabled={validRows.length === 0 || committing}
                    onClick={handleCommit}
                >
                    {committing ? t('Processing') : t('ClientItemsExcelCommit')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default ClientItemsExcelImportModal;
